import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const CSRSection = () => {
  const initiatives = [
    {
      title: "Éducation & Développement des Compétences",
      description: "Nous nous associons avec des institutions éducatives locales pour fournir des bourses, une formation professionnelle et des opportunités de stage pour les jeunes professionnels congolais. Notre engagement envers l'éducation aide à construire la prochaine génération de leaders d'affaires.",
      icon: "GraduationCap",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fbe0e6f7-1764656197139.png",
      imageAlt: "Groupe d'étudiants africains dans une salle de classe avec ordinateurs portables et livres engagés dans une session d'apprentissage collaboratif",
      impact: "200+ étudiants soutenus annuellement"
    },
    {
      title: "Emploi Local & Croissance Économique",
      description: "Nous priorisons l'embauche de talents locaux et le soutien aux entreprises congolaises à travers notre chaîne d'approvisionnement. En créant des opportunités d'emploi de qualité et des partenariats commerciaux équitables, nous contribuons directement au développement économique de la RDC.",
      icon: "Briefcase",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_171170bd3-1764687877901.png",
      imageAlt: "Équipe diversifiée de professionnels africains collaborant dans un environnement de bureau moderne avec ordinateurs portables et documents",
      impact: "150+ employés locaux, 50+ fournisseurs locaux"
    },
    {
      title: "Durabilité Environnementale",
      description: "Nous mettons en œuvre des pratiques écologiques dans toutes nos opérations, des espaces de bureau économes en énergie à la gestion durable de la flotte de véhicules. Notre engagement envers la responsabilité environnementale garantit que nous protégeons les ressources naturelles de la RDC pour les générations futures.",
      icon: "TreePine",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_13c90d242-1765095064354.png",
      imageAlt: "Paysage forestier verdoyant avec grands arbres et végétation naturelle représentant les efforts de conservation environnementale",
      impact: "30% de réduction de l'empreinte carbone depuis 2020"
    },
    {
      title: "Santé & Bien-être Communautaire",
      description: "Nous soutenons les initiatives de santé dans les communautés mal desservies grâce à des partenariats avec des organisations de santé locales. Nos programmes se concentrent sur les soins préventifs, l'éducation à la santé et l'amélioration de l'accès aux services médicaux.",
      icon: "Heart",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1dfa89059-1767197235564.png",
      imageAlt: "Travailleur de la santé en blouse blanche fournissant une consultation médicale à un patient dans un cadre de clinique moderne et propre",
      impact: "5 000+ membres de la communauté touchés"
    }
  ];

  return (
    <section className="py-20 lg:py-28 bg-slate-100 dark:bg-slate-900 relative overflow-hidden">
      {/* Noise texture background */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat',
        }}
      />

      <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16 relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-12 lg:mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
        >
          <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-5 py-2.5 mb-6">
            <Icon name="HeartHandshake" size={20} className="text-blue-400" />
            <span className="text-sm font-medium text-blue-600 dark:text-blue-300">Responsabilité Sociale d'Entreprise</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold text-slate-900 dark:text-white mb-5">
            Construire une Meilleure{' '}
            <span className="bg-gradient-to-r from-blue-400 to-amber-400 bg-clip-text text-transparent">
              RDC Ensemble
            </span>
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-3xl mx-auto leading-relaxed">
            Notre succès est lié à la prospérité des communautés que nous servons. Nous nous engageons à avoir un impact positif et durable sur la République Démocratique du Congo.
          </p>
        </motion.div>

        {/* Initiatives */}
        <div className="space-y-16 lg:space-y-24">
          {initiatives?.map((initiative, index) => (
            <motion.div
              key={index}
              className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 lg:gap-14 items-center`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              {/* Image */}
              <div className="w-full lg:w-1/2">
                <div className="relative h-72 md:h-80 lg:h-[420px] rounded-3xl overflow-hidden shadow-2xl group">
                  <Image
                    src={initiative?.image}
                    alt={initiative?.imageAlt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/20 to-transparent" />
                  <div className="absolute bottom-5 left-6 right-6">
                    <div className="flex items-center gap-3 text-white">
                      <Icon name="TrendingUp" size={20} className="text-white/80" />
                      <span className="text-sm font-semibold">{initiative?.impact}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content Card */}
              <div className="w-full lg:w-1/2">
                <motion.div
                  className="bg-white/80 dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-3xl p-8 md:p-10 shadow-2xl"
                  initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.6, delay: 0.25 }}
                >
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-600/20">
                      <Icon name={initiative?.icon} size={28} className="text-white" />
                    </div>
                    <h3 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
                      {initiative?.title}
                    </h3>
                  </div>

                  <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-6 text-base">
                    {initiative?.description}
                  </p>

                  <div className="flex items-center gap-2 text-amber-400">
                    <Icon name="CheckCircle" size={20} className="text-amber-400" />
                    <span className="text-sm font-semibold">Active Initiative</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="mt-20 lg:mt-28 bg-white/80 dark:bg-slate-800/60 backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-[2.5rem] p-10 md:p-16 text-center"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center mx-auto mb-8 shadow-lg shadow-blue-600/20">
            <Icon name="Globe" size={40} className="text-white" />
          </div>
          <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white mb-4">
            Rejoignez-nous pour Faire la Différence
          </h3>
          <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto mb-10 text-base leading-relaxed">
            Nous croyons que le succès commercial et la responsabilité sociale vont de pair. Associez-vous à ORAZEK pour soutenir des initiatives qui créent un changement positif durable en RDC.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-full font-semibold shadow-lg shadow-blue-600/25 transition-all duration-300 flex items-center justify-center gap-2">
              <Icon name="Mail" size={20} />
              <span>Contacter l'Équipe RSE</span>
            </button>
            <button className="px-8 py-4 border border-slate-200 dark:border-white/20 text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-white/5 rounded-full font-semibold transition-all duration-300 flex items-center justify-center gap-2">
              <Icon name="Download" size={20} />
              <span>Télécharger le Rapport RSE</span>
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CSRSection;
